import { useQuery } from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type { TcScenarioInventorySummary } from 'types/dps-capture';

/** Query-key prefix for the org-wide inventory-summary read (03-CAP endpoint 1). */
export const INVENTORY_SUMMARY_QUERY_KEY = 'dps:inventory-summary' as const;

/** What {@link useInventorySummary} returns. */
export interface UseInventorySummary {
  /** The org-wide counts, or null while loading / before a scenario is picked. */
  summary: TcScenarioInventorySummary | null;
  /** True while the read is in flight. */
  loading: boolean;
  /** The failed read, or null. */
  error: Error | null;
}

/**
 * Loads the org-wide scope-chip counts for the selected Type Curve Scenario —
 * `GET /type_curve_scenarios/:id/inventory_summary`, computed on request.
 *
 * A NULL selection is the prompt state: the query is DISABLED, so no request
 * fires and `summary` stays null (the chips render "select a scenario"). This is
 * AC 1 — no compute without a selection.
 *
 * @param {number | null} typeCurveScenarioId - The selected scenario, or null.
 * @return {UseInventorySummary} The counts, load state, and any error.
 */
export function useInventorySummary(
  typeCurveScenarioId: number | null
): UseInventorySummary {
  const { data, isFetching, error } = useQuery<
    TcScenarioInventorySummary,
    Error
  >({
    queryKey: [INVENTORY_SUMMARY_QUERY_KEY, typeCurveScenarioId],
    enabled: typeCurveScenarioId !== null,
    queryFn: ({ signal }) =>
      api
        .get(`type_curve_scenarios/${typeCurveScenarioId}/inventory_summary`, {
          signal,
        })
        .json<TcScenarioInventorySummary>(),
    // Inventory changes only when the upstream generator rewrites it, so a
    // revisit within the window need not re-hit the server.
    staleTime: 300_000,
  });

  return {
    summary: data ?? null,
    loading: typeCurveScenarioId !== null && isFetching,
    error: error ?? null,
  };
}
